import { type JSONData } from "../base_classes/dto.d";
import { type IMargin } from "../entities/airline_order/dto.gen.d";

export class MarginValue {
  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////

  /**
   * The text value of the margin.
   */
  value: {
    floatValue: number;
    textValue: string;
  } = {
    floatValue: 0,
    textValue: "",
  };

  /**
   * The attribute values associated with the margin value.
   */
  attributes: Record<string, string> = {};

  /// //////////////////////////////
  /// ///////// METHODS ////////////
  /// //////////////////////////////

  /**
   * Set the MarginValue's properties
   */
  set(data: unknown): void {
    if (typeof data !== "object") {
      return;
    }
    const json = data as JSONData;
    const value = json.value as JSONData;

    this.value.floatValue =
      parseFloat(JSON.stringify(value?.floatValue) ?? "0") ?? 0;
    this.value.textValue = (value?.textValue as string) || "";

    this.attributes = (json.attributes as Record<string, string>) ?? {};
  }

  /**
   * Parse to JSON
   */
  toJSON(): JSONData {
    return {
      value: this.value,
    };
  }
}

export class Margin {
  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////

  /**
   * The type of margin.
   */
  type: MarginType = MarginType.PERCENTAGE;

  /**
   * The amount of margin.
   */
  amount: number = 0;

  /**
   * The input type of the margin.
   */
  inputType: InputType = InputType.STANDARD;

  /**
   * The percentage of margin.
   */
  percentage: number = 0;

  /**
   * The type of percentage.
   */
  percentageType: PercentageType = PercentageType.PROFIT_MARGIN;

  /**
   * Whether the margin is staggered.
   */
  staggered: boolean = false;

  /**
   * The values of the margin.
   */
  values: MarginValue[] = [];

  /// //////////////////////////////
  /// ///////// METHODS ////////////
  /// //////////////////////////////

  /**
   * Set the Margin's properties
   */
  set(data: unknown): void {
    if (typeof data !== "object") {
      return;
    }
    const json = data as JSONData;
    this.type = (json.type as MarginType) || MarginType.PERCENTAGE;
    this.amount = parseFloat(json.amount?.toString() ?? "0") ?? 0;
    this.percentage = parseFloat(json.percentage?.toString() ?? "0") ?? 0;
    this.percentageType =
      (json.percentageType as PercentageType) || PercentageType.PROFIT_MARGIN;
    this.staggered = (json.staggered as boolean) || false;
    if (Array.isArray(json.values)) {
      this.values = json.values.map((value) => {
        const marginValue = new MarginValue();
        marginValue.set(value);
        return marginValue;
      });
    }
  }

  /**
   * Parse to JSON
   */
  toJSON(): JSONData {
    return {
      type: this.type,
      amount: parseFloat(this.amount?.toString().replace(/[^0-9]/g, "")),
      percentage: parseFloat(this.percentage?.toString().replace(/[^0-9]/g, "")),
      percentageType: this.percentageType,
      staggered: this.staggered,
      values: this.values.map((value) => value.toJSON()),
    };
  }

  /**
   * Select the correct margin value
   * @param units The amount or option of units provided
   * @param marginInfo The information available about the margin
   * @returns {number} The numerical margin value (amount or percentage, dependent on settings)
   */
  static SelectMarginValue(units: string | number, marginInfo: IMargin) {
    // Select margin value
    let marginValue = 0;
    if (marginInfo.staggered) {
      marginValue =
        marginInfo.values.find(
          ({ attributes }: any) => attributes.units === units,
        )?.value?.floatValue || 0;
    } else {
      marginValue =
        marginInfo.type === "percentage"
          ? marginInfo.percentage
          : marginInfo.amount;
    }

    return marginValue;
  }

  /**
   * Calculate the margin and final price based on the total costs and margin information.
   * @param totalCosts The amount over which to calculate the margin.
   * @param marginValue The numerical margin value (amount or percentage, dependent on settings).
   * @param marginInfo The information available about the margin.
   * @returns {{ totalCosts, marginAmount, marginPercentage, finalPrice }} The calculated values.
   */
  static CalculateTotals(
    totalCosts: number,
    marginValue: number,
    marginInfo: IMargin,
  ) {
    let marginAmount = 0;
    let marginPercentage = 0;
    let finalPrice = 0;

    const isPercentageType = marginInfo.type === MarginType.PERCENTAGE;

    if (isPercentageType) {
      const isBrutoProfitMargin =
        marginInfo.percentageType === PercentageType.BRUTO_PROFIT_MARGIN;

      if (isBrutoProfitMargin) {
        // Calculate for BRUTO_PROFIT_MARGIN
        const factor = marginValue / 100;
        marginAmount = (totalCosts / (1 - factor)) * factor;
        finalPrice = totalCosts + marginAmount;
      } else {
        // Calculate for standard percentage margin
        marginAmount = totalCosts * (marginValue / 100);
        finalPrice = totalCosts + marginAmount;
      }
    } else {
      // Calculate for fixed margin amount
      marginAmount = marginValue;
      finalPrice = totalCosts + marginAmount;
    }

    // Calculate margin percentage based on final price
    marginPercentage = (marginAmount / finalPrice) * 100;
    return {
      totalCosts,
      marginAmount,
      marginPercentage,
      finalPrice,
    };
  }
}

/**
 * List of percentage types for Margin
 */
export enum PercentageType {
  PROFIT_MARGIN = "profit-margin",
  BRUTO_PROFIT_MARGIN = "bruto-profit-margin",
}

export const PercentageTypeValues: Record<string, PercentageType> =
  Object.freeze({
    "profit-margin": PercentageType.PROFIT_MARGIN,
    "bruto-profit-margin": PercentageType.BRUTO_PROFIT_MARGIN,
  });

export enum InputType {
  STAGGERED = "staggered",
  STANDARD = "standard",
}

export const InputTypeValues: Record<string, InputType> = Object.freeze({
  staggered: InputType.STAGGERED,
  standard: InputType.STANDARD,
});

export enum MarginType {
  PERCENTAGE = "percentage",
  AMOUNT = "amount",
}
export const MarginTypeValues = Object.freeze({
  PERCENTAGE: "percentage",
  AMOUNT: "amount",
});

export const MarginLevels = Object.freeze({
  STANDARD: "standard",
  PREMIUM: "premium",
  DIRECT: "direct",
});
